import { useState } from 'react';

import Alert from '@mui/material/Alert';

import Filter from '@webclient/components/pages/home/sections/bots/content/filter';
import StyledStack from '@webclient/components/pages/home/sections/bots/content/styles';
import Header from '@webclient/components/pages/home/sections/bots/header';
import List from '@webclient/components/pages/home/sections/bots/list';

import useGetBots from '@webclient/hooks/useGetBots';
import useToggleState from '@webclient/hooks/useToggleState';

import {
  AllBotsCategoryType,
  AllBotsCreatorType,
} from '@webclient/shared-types/bots';

import XCircleIcon from '~icons/knowz-iconify/x-circle';

import { IconButton } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';

export default function Content() {
  const { state: isDescending, toggle: toggleOrder } = useToggleState(true);
  const [category, setCategory] = useState<AllBotsCategoryType>('All');
  const [creator, setCreator] = useState<AllBotsCreatorType>('Everyone');
  const { t } = useTranslate();
  const {
    data: botsData,
    isLoading: isBotsDataLoading,
    isError: isBotsDataError,
  } = useGetBots({
    order: isDescending ? 'DESC' : 'ASC',
    category:
      category === 'All' || category === 'Knowz AI' ? undefined : category,
    creator: creator === 'Everyone' ? undefined : creator,
    shouldShuffle: true,
    shouldPin: true,
    shouldIncludeKnowzAI: true,
  });

  function handleFilterByCategory(category: AllBotsCategoryType) {
    setCategory(category);
  }

  function handleFilterByCreator(action: AllBotsCreatorType) {
    setCreator(action);
  }

  function handleClearFilter() {
    setCategory('All');
    setCreator('Everyone');
  }

  const shouldShowClearFilters = category !== 'All' || creator !== 'Everyone';

  if (isBotsDataError) {
    return (
      <Alert severity="error">
        {t(
          'response.errors.code.520',
          'An error occurred, please try reloading the page',
        )}
      </Alert>
    );
  }

  return (
    <StyledStack>
      <Header
        PrimaryAction={
          <Filter
            handleFilterByCategory={handleFilterByCategory}
            toggleOrder={toggleOrder}
            handleFilterByCreator={handleFilterByCreator}
            currentCategory={category}
            currentCreator={creator}
            order={isDescending ? 'DESC' : 'ASC'}
          />
        }
        SecondaryAction={
          shouldShowClearFilters && (
            <IconButton
              variant="ghost"
              color="secondary"
              onClick={handleClearFilter}
            >
              <XCircleIcon />
            </IconButton>
          )
        }
        title={t('page.home.gamification.title', 'Search with attitude?')}
        subtitle={t(
          'page.home.gamification.subtitle',
          'Choose a character to bring your searches and chats to life or just get started now with Knowz AI',
        )}
      />

      <List
        bots={botsData?.data || []}
        isLoading={isBotsDataLoading}
      />
    </StyledStack>
  );
}
